import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaEdit, FaTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import "../assets/css/ViewCustomer.css";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";
import { useConfirm } from "../components/ConfirmModal";

function ViewProjectPayment({ paymentId, onBack }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const role = (() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();

  const canEdit = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);

  useEffect(() => {
    const load = async () => {
      if (!paymentId) return;
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/project-payments/${paymentId}`);
        const p = res.data?.data ?? res.data?.payment ?? res.data;
        setPayment(p || null);
      } catch (err) {
        console.error("Error fetching project payment:", err);
        alert("Failed to load payment details.");
        setPayment(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [paymentId]);

  const InfoItem = ({ label, value }) => {
    const hasValue = value !== undefined && value !== null && String(value).trim() !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
    } catch {
      return "—";
    }
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div>Loading payment details...</div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="viewlead-container">
        <div>Payment not found.</div>
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>
    );
  }

  const p = payment;

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>

      <div className="viewlead-card">
        <h3 className="viewlead-section-title" style={{ marginTop: 0 }}>Payment Details</h3>

        {canEdit && (
          <div style={{ display: "flex", gap: 10, marginBottom: 14, justifyContent: "flex-end" }}>
            <button
              type="button"
              className="btn-edit-followup"
              onClick={() => {
                const pid = payment?.Payment_Id ?? payment?.payment_id ?? payment?.id;
                if (pid != null) navigate(`/project-payments/${pid}/edit`);
              }}
            >
              <FaEdit style={{ marginRight: 6 }} />
              Edit
            </button>
            <button
              type="button"
              className="btn-delete-followup"
              disabled={deleting}
              onClick={async () => {
                const pid = payment?.Payment_Id ?? payment?.payment_id ?? payment?.id;
                const projId = payment?.Project_Id ?? payment?.project_id;
                if (pid == null) return;
                if (!(await confirm({ title: "Delete Payment", message: "Are you sure you want to delete this payment?" }))) return;
                setDeleting(true);
                try {
                  const res = await axiosInstance.delete(`/delete-project-payment/${pid}`);
                  alert(res?.data?.message || "Payment deleted successfully.");
                  const refresh = Date.now();
                  if (projId != null) navigate(`/projects/${projId}/view?refresh=${refresh}`);
                  else navigate(-1);
                } catch (err) {
                  console.error("Error deleting project payment:", err);
                  alert(
                    err.response?.data?.message ||
                      err.response?.data?.error ||
                      err.message ||
                      "Failed to delete payment."
                  );
                } finally {
                  setDeleting(false);
                }
              }}
            >
              <FaTrashAlt style={{ marginRight: 6 }} />
              Delete
            </button>
          </div>
        )}

        <div className="viewlead-info-grid">
          <InfoItem label="Payment ID" value={p.Payment_Id ?? p.payment_id ?? p.id ?? "—"} />
          <InfoItem label="Project" value={p.Project_Name ?? p.project_name ?? "—"} />
          <InfoItem label="Customer" value={p.Customer_Name ?? p.customer_name ?? "—"} />
        </div>

        <h3 className="viewlead-section-title">Payment Info</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Payment Date" value={formatDate(p.Payment_Date ?? p.payment_date)} />
          <InfoItem label="Amount" value={p.Amount ?? p.amount ?? "—"} />
          <InfoItem label="Currency" value={p.Currency_Code ?? p.currency_code ?? p.Currency_Name ?? p.currency_name ?? "—"} />
          <InfoItem label="Payment Mode" value={p.Payment_Mode_Name ?? p.payment_mode_name ?? "—"} />
          <InfoItem label="Payment Type" value={p.Payment_Type ?? p.payment_type ?? "—"} />
          <InfoItem label="Payment Status" value={p.Payment_Status ?? p.payment_status ?? "—"} />
          <InfoItem label="Total Amount" value={p.Total_Amount ?? p.total_amount ?? "—"} />
          <InfoItem label="Transaction Ref" value={p.Transaction_Ref ?? p.transaction_ref ?? "—"} />
        </div>

        <h3 className="viewlead-section-title">Remarks</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Remarks" value={p.Remarks ?? p.remarks ?? "—"} />
        </div>

        <h3 className="viewlead-section-title">Audit</h3>
        <div className="viewlead-info-grid">
          <InfoItem label="Created By" value={p.Created_By_Name ?? p.created_by_name ?? p.Created_By ?? "—"} />
          <InfoItem label="Created On" value={formatDate(p.Created_On ?? p.created_on)} />
          <InfoItem label="Updated By" value={p.Updated_By_Name ?? p.updated_by_name ?? p.Updated_By ?? "—"} />
          <InfoItem label="Updated On" value={formatDate(p.Updated_On ?? p.updated_on)} />
        </div>
      </div>
    </div>
  );
}

export default ViewProjectPayment;


